"use client";

import { SalesChannelBranding } from "@lib/data/sales-channel";
import { createContext, ReactNode, useContext } from "react";

interface BrandingContextType {
  branding: SalesChannelBranding;
  // Helper methods
  getLogo: (variant?: "light" | "dark") => string | undefined;
  getColor: (type: "primary" | "secondary" | "accent") => string;
  getSocialLink: (platform: "facebook" | "zalo" | "messenger" | "instagram" | "youtube") => string | undefined;
}

const BrandingContext = createContext<BrandingContextType | null>(null);

interface BrandingProviderProps {
  children: ReactNode;
  branding: SalesChannelBranding;
}

export function BrandingProvider({ children, branding }: BrandingProviderProps) {
  const getLogo = (variant: "light" | "dark" = "light") => {
    return variant === "dark" ? branding.logo_dark_url || branding.logo_url : branding.logo_url;
  };

  const getColor = (type: "primary" | "secondary" | "accent") => {
    const colorMap = {
      primary: branding.primary_color || "#000000",
      secondary: branding.secondary_color || "#ffffff",
      accent: branding.accent_color || branding.primary_color || "#000000",
    };
    return colorMap[type];
  };

  const getSocialLink = (platform: "facebook" | "zalo" | "messenger" | "instagram" | "youtube") => {
    const linkMap = {
      facebook: branding.facebook_url,
      zalo: branding.zalo_id ? `https://zalo.me/${branding.zalo_id}` : undefined,
      messenger: branding.messenger_url,
      instagram: branding.instagram_url,
      youtube: branding.youtube_url,
    };
    return linkMap[platform];
  };

  return (
    <BrandingContext.Provider value={{ branding, getLogo, getColor, getSocialLink }}>
      {children}
    </BrandingContext.Provider>
  );
}

export function useBranding() {
  const context = useContext(BrandingContext);
  if (!context) {
    throw new Error("useBranding must be used within a BrandingProvider");
  }
  return context;
}

/**
 * Hook để inject CSS variables từ branding config
 * Sử dụng trong layout để apply màu sắc toàn app
 */
export function useBrandingStyles() {
  const { branding } = useBranding();

  return {
    "--color-primary": branding.primary_color || "#000000",
    "--color-secondary": branding.secondary_color || "#ffffff",
    "--color-accent": branding.accent_color || branding.primary_color || "#000000",
  } as React.CSSProperties;
}
